import type { Cache, CacheEvents } from "@algolia/cache-common"
import type { QueryClient } from "@tanstack/react-query"

export function reactQueryCache(queryClient: QueryClient): Cache {
  function getRequestsKey(key: object | string): object | string {
    if (typeof key === "object" && "request" in key) {
      return key.request as object
    } else {
      return key
    }
  }

  return {
    get<TValue>(
      key: object | string,
      defaultValue: () => Readonly<Promise<TValue>>,
      events: CacheEvents<TValue> = {
        miss: () => Promise.resolve(),
      }
    ): Readonly<Promise<TValue>> {
      const requestKey = getRequestsKey(key)

      const querycache = queryClient.getQueryData<TValue>(["algolia", requestKey])

      if (querycache !== undefined) {
        return Promise.resolve(querycache)
      }

      const promise = defaultValue()
      const miss = (events && events.miss) || (() => Promise.resolve())

      return promise.then((value: TValue) => miss(value)).then(() => promise)
    },

    set<TValue>(key: object | string, value: TValue): Readonly<Promise<TValue>> {
      const requestKey = getRequestsKey(key)

      queryClient.setQueryData(["algolia", requestKey], value)

      return Promise.resolve(value)
    },

    delete(key: object | string): Readonly<Promise<void>> {
      const requestKey = getRequestsKey(key)

      queryClient.removeQueries({ queryKey: ["algolia", requestKey] })

      return Promise.resolve()
    },

    clear(): Readonly<Promise<void>> {
      return Promise.resolve()
    },
  }
}
